import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import type {
	LWApiPublicBillingPaymentInstrumentListParamsRaw,
	LWApiPublicBillingPaymentInstrumentListResultRaw,
} from '@liquidweb-dev/club-sauce/public/billing/payment/instrument/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import { fromApiBoolean } from '@liquidweb-dev/club-sauce/types/boolean';
import api from 'modules/queries/api';

export const BILLING_PAYMENT_INSTRUMENT_LIST_QUERY_KEY =
	'/billing/payment/instrument/list';

const precedence = (
	instrument: LWApiPublicBillingPaymentInstrumentListResultRaw['items'][number],
) => {
	if (fromApiBoolean(instrument.is_default)) return 0;
	if (fromApiBoolean(instrument.is_backup)) return 1;
	return 2;
};

/**
 * Sorts the default first and the backup second. The sort is stable, so the rest keep
 * the order the API returned them in.
 */
export const sortByPrecedence = (
	data: LWApiPublicBillingPaymentInstrumentListResultRaw,
): LWApiPublicBillingPaymentInstrumentListResultRaw => ({
	...data,
	items: [...data.items].sort((a, b) => precedence(a) - precedence(b)),
});

/**
 * Lists the payment instruments on file. The account is injected from the session —
 * never pass `accnt` or `subaccnt`, which the server rejects rather than ignores.
 *
 * `params` is carried through to the query key, so `alsowith` is a caller's choice rather
 * than the hook's, and two different `alsowith` values cache as separate entries instead
 * of clobbering each other. Undeclared input keys raise `LW::Exception::Input::Unknown`,
 * so only a server-declared `alsowith` may be sent; `cardDetails` and
 * `lastTransactionStatus` are both declared.
 *
 * The result is deliberately unpaginated — `{ items }` with no paging envelope.
 *
 * Items come back sorted by {@link sortByPrecedence}. A caller's `select` replaces that
 * sort, so a caller that wants to keep the order composes with `sortByPrecedence`.
 */
const useBillingPaymentInstrumentList = (
	params: LWApiPublicBillingPaymentInstrumentListParamsRaw,
	options?: UseQueryOptions<
		LWApiPublicBillingPaymentInstrumentListResultRaw,
		ApiErrorI
	>,
) =>
	useQuery({
		queryKey: [BILLING_PAYMENT_INSTRUMENT_LIST_QUERY_KEY, params],
		queryFn: () => api.billing.payment.instrument.apiRaw.list(params),
		select: sortByPrecedence,
		...options,
	});

export default useBillingPaymentInstrumentList;
